import Trix from "trix";
import { forwardRef, useRef } from "react";
import { RegisterOptions } from "react-hook-form";
import TrixEditor from "components/FormElements/Fields/TrixTextAreaField/TrixEditor";
import { RichText } from "components/FormElements/Fields/TrixTextAreaField/trixrichtext";
import useModal from "hooks/useModal";
import { LegacyTargetType } from "features/RecordTarget/models";
import { ActionType } from "features/Resources/Shared/models";
import PersonalizationTokenMenu from "components/FormElements/Fields/TemplateRichTextAreaField/PersonalizationTokenMenu";
import { useFloatingField } from "./context";

type FloatingTrixProps = {
  control: any;
  name: string;
  rules?: RegisterOptions;
  onInitialize?: (event: Event, richText: RichText) => void;
  placeholder?: string;
  isEditable?: boolean;
  hideToolbar?: boolean;
  alwaysShowToolbar?: boolean;
  maintainToolbarSpace?: boolean;
  includeAttachment?: boolean;
  hideHeading?: boolean;
  emailHeadingSizes?: boolean;
  toolbarBgColor?: string;
  editorBgColor?: string;
  /** When true, adds the Personalize button and token-picker modal. Requires actionType. */
  includePersonalize?: boolean;
  actionType?: ActionType;
  targetType?: LegacyTargetType;
};

const FloatingTrix = forwardRef<Trix, FloatingTrixProps>(function FloatingTrix(
  {
    control,
    name,
    maintainToolbarSpace = true,
    includePersonalize,
    actionType,
    targetType,
    onInitialize,
    ...rest
  },
  ref,
) {
  const { setHasValue } = useFloatingField();
  const internalRef = useRef<any>(null);
  const trixRef = ref ?? internalRef;

  const {
    open: openTokenModal,
    close: closeTokenModal,
    Modal: TokenModal,
  } = useModal({
    headerTitle: "Insert Token",
    headerFontSize: "lg",
    size: "lg",
    isCentered: true,
  });

  const handleInitialize = (event: Event, richText: RichText) => {
    if (includePersonalize) {
      richText.insertPersonalizationElements();
      richText.personalizationTokenButton?.addEventListener("click", () => {
        openTokenModal();
      });
    }
    onInitialize?.(event, richText);
  };

  return (
    <>
      <TrixEditor
        name={name}
        control={control}
        trixRef={trixRef}
        onHasValueChange={setHasValue}
        maintainToolbarSpace={maintainToolbarSpace}
        onInitialize={handleInitialize}
        {...rest}
      />
      {includePersonalize && actionType && (
        <TokenModal>
          <PersonalizationTokenMenu
            actionType={actionType}
            targetType={targetType}
            onSelect={(token: any) => {
              const editor = (trixRef as any)?.current?.editor;
              if (editor) {
                const attachment = new Trix.Attachment({ content: token.content, sgid: token.sgid });
                editor.insertAttachment(attachment);
              }
              closeTokenModal();
            }}
          />
        </TokenModal>
      )}
    </>
  );
});

FloatingTrix.displayName = "FloatingTrix";
export default FloatingTrix;
